import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { Check, LoaderCircle, RefreshCw, X } from 'lucide-react'
import AvatarImage from '../AvatarImage'
import Button from '../ui/Button'
import { closeLoadingModal, showConfirmModal, showErrorModal, showLoadingModal, showSuccessModal } from '../ui/ConfirmDialog'
import {
  fetchPullRequests,
  restoreDeletedPullRequestChange,
  reviewPullRequestChanges,
  resolvePullRequestConflict,
  withdrawPullRequestChange,
  type PullRequestChange,
  type PullRequestDTO,
} from '../../services/pullRequestService'
import type { ProjectUser, ProjectMergeConflict } from '../../services/projectService'
import { getApiMessage } from '../../utils/apiMessage'
import { formatDiffValue } from '../../utils/codeSheetDiff'
import { buildChangeDescription, mapApiChangeTypeToOperation } from '../../utils/changesetPreview'
import ChangesetChangePreview from './ChangesetChangePreview'

type ChangeStatus = 'pending' | 'approved' | 'rejected'
type ChangeType = 'major' | 'revision'
type ReviewAction = 'approve' | 'reject'
type ReviewActionPhase = 'processing' | 'success' | 'error'

interface ReviewActionState {
  action: ReviewAction
  phase: ReviewActionPhase
  error?: string | null
}

const REVIEW_SUCCESS_DISPLAY_MS = 450

export type { ChangeStatus, ChangeType }

export interface Change {
  id: string
  status: ChangeStatus
  type: ChangeType
  nodeType: string
  nodeId?: string | null
  // Every node covered by a rolled-up change (an appendix with its whole table).
  nodeIds?: string[] | null
  changeType: number | string
  path?: string | null
  oldValue?: string | null
  newValue?: string | null
  description: string
  approvalComment?: string | null
  authorName: string
  authorAvatarUri?: string | null
  projectName: string
  targetRevision?: string | null
  pullRequestId: string
  changesetCode: string
  changesetDescription?: string | null
  conflict?: ProjectMergeConflict | null
}

export interface ChangesRequestedDrawerProps {
  open: boolean
  onClose: () => void
  codeIndexId?: string
  projectId?: string
  refreshKey?: number
  hasApprovalPermission?: boolean
  approvalDisabledReason?: string | null
  onRefresh?: () => void | Promise<void>
  canReviseRejectedChanges?: boolean
  revisionDisabledReason?: string | null
  onReviseChange?: (change: Change) => void | Promise<void>
  onGoToChange?: (change: Change) => void
  canWithdrawChanges?: boolean
  withdrawDisabledReason?: string | null
  // "B — Fan curves" for an appendix change's node id; the letter is only known on the codesheet page.
  resolveAppendixLabel?: (nodeId: string | null | undefined) => string | null
}

export type ResolveAppendixLabel = NonNullable<ChangesRequestedDrawerProps['resolveAppendixLabel']>

function isAppendixChange(change: Pick<Change, 'nodeType' | 'path'>): boolean {
  return change.nodeType === 'Appendix' && change.path !== 'defines_valid_combinations'
}

function isDeleteChange(changeType: number | string): boolean {
  return changeType === 2 || String(changeType).trim().toLowerCase() === 'delete'
}

function mapReviewStatus(status: number | string | null | undefined): ChangeStatus {
  if (typeof status === 'string') {
    const normalized = status.trim().toLowerCase()
    if (normalized === 'approved') return 'approved'
    if (normalized === 'rejected') return 'rejected'
    return 'pending'
  }

  if (status === 1) return 'approved'
  if (status === 2) return 'rejected'
  return 'pending'
}

function extractShortCode(id: string | null | undefined): string {
  const normalized = String(id || '').trim()
  if (!normalized) return 'CS'
  const parts = normalized.split(':')
  return `CS-${parts.length >= 3 ? parts[2] : normalized.slice(-5)}`
}

function formatReviewLine(change: Change): string {
  return change.targetRevision ? `Revision ${change.targetRevision}` : 'Revision'
}

function getChangeDescription(
  change: PullRequestChange,
  pullRequest: PullRequestDTO,
  resolveAppendixLabel?: ResolveAppendixLabel
): string {
  // An appendix change covers a whole table, so it describes itself instead of repeating the PR text.
  if (change.nodeType === 'Appendix') {
    return buildChangeDescription({
      operation: mapApiChangeTypeToOperation(change.changeType),
      nodeType: change.nodeType,
      nodeId: change.nodeId ?? null,
      path: change.path ?? null,
      oldValue: change.oldValue ?? null,
      newValue: change.newValue ?? null,
      appendixLabel: resolveAppendixLabel?.(change.nodeId) ?? null,
    })
  }

  const description = String(pullRequest.description || pullRequest.changeset?.description || '').trim()
  if (description) return description

  const nodeType = String(change.nodeType || 'Code sheet').trim()
  return `${nodeType} change`
}

function formatChangeSummary(change: Change): string {
  const revision = String(change.targetRevision || '').trim()
  const description = String(change.description || '').trim()
  if (revision && description) return `${revision} - ${description}`
  return revision || description || 'Change'
}

function getAuthorDisplayName(author?: ProjectUser | null): string | null {
  const name = String(author?.name || '').trim()
  if (name) return name
  const email = String(author?.emailAddress || '').trim()
  return email || null
}

function mapPullRequestChanges(pullRequests: PullRequestDTO[], resolveAppendixLabel?: ResolveAppendixLabel): Change[] {
  return pullRequests.flatMap((pullRequest) => {
    const changeset = pullRequest.changeset
    const changes = Array.isArray(changeset?.changes) ? changeset?.changes ?? [] : []
    const changesetCode = extractShortCode(changeset?.id || pullRequest.id)
    const projectName = pullRequest.sourceBranch?.name || pullRequest.title || pullRequest.id || 'Project'
    const targetRevision = pullRequest.sourceBranch?.targetRevision || null
    const isMajor = Boolean(changeset?.isMajor)

    return changes
      .filter((change): change is PullRequestChange & { id: string } => Boolean(change.id) && !change.withdrawnAt)
      .map((change) => {
        return {
          id: change.id,
          status: mapReviewStatus(change.approvalStatus),
          type: isMajor ? 'major' : 'revision',
          nodeType: change.nodeType || 'Unknown',
          nodeId: change.nodeId ?? null,
          nodeIds: Array.isArray(change.nodeIds) ? change.nodeIds : null,
          changeType: change.changeType,
          path: change.path ?? null,
          oldValue: change.oldValue ?? null,
          newValue: change.newValue ?? null,
          description: getChangeDescription(change, pullRequest, resolveAppendixLabel),
          approvalComment: change.approvalComment ?? changeset?.approvalComment ?? null,
          authorName: getAuthorDisplayName(change.createdBy)
            || getAuthorDisplayName(changeset?.createdBy)
            || getAuthorDisplayName(pullRequest.createdBy)
            || getAuthorDisplayName(pullRequest.sourceBranch?.createdBy)
            || 'Unknown User',
          authorAvatarUri: change.createdBy?.avatarUri
            || changeset?.createdBy?.avatarUri
            || pullRequest.createdBy?.avatarUri
            || pullRequest.sourceBranch?.createdBy?.avatarUri
            || null,
          projectName,
          targetRevision,
          pullRequestId: pullRequest.id || '',
          changesetCode,
          changesetDescription: pullRequest.description || changeset?.description || null,
          conflict: pullRequest.conflicts?.conflicts.find(conflict => (
            isAppendixChange(change)
              ? (conflict.nodeId !== change.nodeId || conflict.path !== 'defines_valid_combinations')
                && (conflict.nodeId === change.nodeId || (change.nodeIds || []).includes(conflict.nodeId))
              : conflict.nodeId === change.nodeId && (!conflict.path || !change.path || conflict.path === change.path)
          )),
        } satisfies Change
      })
  })
}

function getReviewActionKey(change: Pick<Change, 'pullRequestId' | 'id'>): string {
  return `${change.pullRequestId}:${change.id}`
}

function applyLocalReviewDecision(
  pullRequests: PullRequestDTO[],
  change: Change,
  decision: 'Approved' | 'Rejected',
  comment: string | null
): PullRequestDTO[] {
  return pullRequests.map((pullRequest) => {
    if (pullRequest.id !== change.pullRequestId || !pullRequest.changeset?.changes) return pullRequest

    return {
      ...pullRequest,
      changeset: {
        ...pullRequest.changeset,
        changes: pullRequest.changeset.changes.map((candidate) => (
          candidate.id === change.id
            ? {
                ...candidate,
                approvalStatus: decision,
                approvalComment: comment,
              }
            : candidate
        )),
      },
    }
  })
}

function waitForReviewSuccessIndicator(): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, REVIEW_SUCCESS_DISPLAY_MS))
}

const ChangeTypeBadge: React.FC<{ type: ChangeType }> = ({ type }) => {
  const isMajor = type === 'major'
  return (
    <span className="inline-flex items-center rounded-full bg-[#E9F2FB] px-2 py-0.5 text-xs font-semibold text-[#1D5176]">
      {isMajor ? 'Major' : 'Revision'}
    </span>
  )
}

interface ChangeReviewCardProps {
  change: Change
  hasPermission: boolean
  approvalDisabledReason?: string | null
  isProcessing: boolean
  reviewActionState?: ReviewActionState
  onApprove: (change: Change) => Promise<void>
  onReject: (change: Change) => void
  onGoToChange?: (change: Change) => void
  canRevise: boolean
  revisionDisabledReason?: string | null
  onRevise: (change: Change) => Promise<void>
  canWithdraw: boolean
  withdrawDisabledReason?: string | null
  onWithdraw: (change: Change) => Promise<void>
  onResolve: (change: Change, resolution: 'UseMain' | 'KeepProject') => Promise<void>
}

const ChangeReviewCard: React.FC<ChangeReviewCardProps> = ({
  change,
  hasPermission,
  approvalDisabledReason = null,
  isProcessing,
  reviewActionState,
  onApprove,
  onReject,
  onGoToChange,
  canRevise,
  revisionDisabledReason = null,
  onRevise,
  canWithdraw,
  withdrawDisabledReason = null,
  onWithdraw,
  onResolve,
}) => {
  const canReview = change.status === 'pending' || (change.status === 'approved' && Boolean(change.conflict))
  const [showTable, setShowTable] = useState(false)
  const appendixChange = isAppendixChange(change)
  // A removed appendix cannot be opened in the editor any more.
  const goToDisabledReason = appendixChange && isDeleteChange(change.changeType)
    ? 'This appendix is no longer in the project.'
    : ''
  const disabledReason = !hasPermission
    ? approvalDisabledReason || 'You do not have permission to perform this action.'
    : !canReview
      ? 'This change has already been reviewed.'
      : ''
  const isReviewBusy = reviewActionState?.phase === 'processing' || reviewActionState?.phase === 'success'
  const isCardBusy = isProcessing || isReviewBusy
  const getActionLabel = (action: ReviewAction): string => {
    if (reviewActionState?.action !== action) return action === 'approve' ? 'Approve' : 'Reject'
    if (reviewActionState.phase === 'processing') return action === 'approve' ? 'Approving change' : 'Rejecting change'
    if (reviewActionState.phase === 'success') return action === 'approve' ? 'Change approved' : 'Change rejected'
    return action === 'approve' ? 'Approve' : 'Reject'
  }
  const renderActionContent = (action: ReviewAction) => {
    if (reviewActionState?.action !== action) return action === 'approve' ? 'Approve' : 'Reject'
    if (reviewActionState.phase === 'processing') {
      return <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
    }
    if (reviewActionState.phase === 'success') {
      return <Check className="h-4 w-4" aria-hidden="true" />
    }
    return action === 'approve' ? 'Approve' : 'Reject'
  }
  const withdrawTooltip = canWithdraw
    ? ''
    : withdrawDisabledReason || 'You do not have permission to withdraw this change.'

  const withdrawButton = (
    <Tooltip title={withdrawTooltip}>
      <span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void onWithdraw(change)}
          disabled={isCardBusy || !canWithdraw}
        >
          Withdraw
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <ChangeTypeBadge type={change.type} />
      </div>

      <div className="mb-2 flex items-center gap-2">
        <AvatarImage name={change.authorName} url={change.authorAvatarUri || null} size={24} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-800">{change.authorName}</div>
          <div className="truncate text-xs text-gray-500">{formatReviewLine(change)}</div>
        </div>
      </div>

      <p className="mb-3 text-sm leading-5 text-gray-800">{formatChangeSummary(change)}</p>

      {change.conflict ? (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-gray-800" data-testid="change-review-conflict">
          <div className="font-semibold">This change conflicts with the live Code Sheet.</div>
          <div className="mt-1">{change.status !== 'rejected'
            ? 'Reject it so the author can resolve the conflict.'
            : change.conflict.canKeepProject && !appendixChange
              ? 'Choose Main or keep the project value and submit it for another review.'
              : 'Revise or withdraw this change, or restore deleted content, to resolve the conflict.'}</div>
          {change.conflict.path ? <>
            <div className="mt-2 break-words">Main: {formatDiffValue(JSON.parse(change.conflict.liveValue || 'null'))}</div>
            <div className="mt-1 break-words">Project: {formatDiffValue(JSON.parse(change.conflict.projectValue || 'null'))}</div>
          </> : <div className="mt-1">{change.conflict.reason}</div>}
        </div>
      ) : null}

      {appendixChange ? (
        <div className="mb-3">
          <button
            type="button"
            className="text-xs font-semibold text-[#1D5176] hover:underline"
            aria-expanded={showTable}
            onClick={() => setShowTable(current => !current)}
          >
            {showTable ? 'Hide table' : 'Show table'}
          </button>
          {showTable ? (
            <div className="mt-2" data-testid="appendix-change-table">
              <ChangesetChangePreview
                change={{
                  operation: mapApiChangeTypeToOperation(change.changeType),
                  nodeType: change.nodeType,
                  nodeId: change.nodeId ?? null,
                  path: change.path ?? null,
                  oldValue: change.oldValue ?? null,
                  newValue: change.newValue ?? null,
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {change.status === 'rejected' ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="rejected-change-message">
          <div className="font-semibold">This change has been rejected.</div>
          <div className="mt-1"><span className="font-semibold">Reason:</span> {change.approvalComment || 'No reason provided.'}</div>
        </div>
      ) : null}

      {reviewActionState?.phase === 'error' && reviewActionState.error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700" role="alert">
          {reviewActionState.error}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        {change.status !== 'rejected' ? (
          <Tooltip title={goToDisabledReason}>
            <span className="mr-auto">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onGoToChange?.(change)}
                disabled={!onGoToChange || Boolean(goToDisabledReason)}
              >
                Go to
              </Button>
            </span>
          </Tooltip>
        ) : null}
        {canReview ? (
          <div className="flex gap-2">
            {withdrawButton}
            <Tooltip title={disabledReason}>
            <span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onReject(change)}
                disabled={isCardBusy || !hasPermission}
                aria-label={getActionLabel('reject')}
                className="min-w-[74px]"
              >
                {renderActionContent('reject')}
              </Button>
            </span>
            </Tooltip>
            <Tooltip title={change.conflict ? 'Resolve this conflict before approving the change.' : disabledReason}>
              <span>
                <Button
                  size="sm"
                  onClick={() => void onApprove(change)}
                  disabled={isCardBusy || !hasPermission || Boolean(change.conflict)}
                  aria-label={getActionLabel('approve')}
                  className="min-w-[78px]"
                >
                  {renderActionContent('approve')}
                </Button>
              </span>
            </Tooltip>
          </div>
        ) : change.status === 'rejected' ? (
          <div className="flex flex-wrap justify-end gap-2">
            {change.conflict?.canKeepProject && !appendixChange ? <>
              <Button size="sm" variant="secondary" disabled={isCardBusy || !canRevise}
                onClick={() => void onResolve(change, 'UseMain')}>Use Main</Button>
              <Button size="sm" disabled={isCardBusy || !canRevise}
                onClick={() => void onResolve(change, 'KeepProject')}>Keep Project &amp; Resubmit</Button>
            </> : null}
            <Tooltip title={canRevise ? '' : revisionDisabledReason || 'You do not have permission to revise this change.'}>
              <span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isCardBusy || !canRevise}
                  onClick={() => void onRevise(change)}
                >
                  {isDeleteChange(change.changeType) ? 'Restore Deleted Change' : 'Revise Change'}
                </Button>
              </span>
            </Tooltip>
            {withdrawButton}
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface RejectChangeDialogProps {
  change: Change | null
  open: boolean
  saving: boolean
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}

const RejectChangeDialog: React.FC<RejectChangeDialogProps> = ({
  change,
  open,
  saving,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setReason('')
    setError(null)
  }, [open])

  const handleConfirm = async () => {
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setError('Reason for rejection is required.')
      return
    }

    await onConfirm(trimmedReason)
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 1 }}>
        <span className="text-xl font-semibold text-gray-900">Reject change</span>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <X className="h-5 w-5" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        <p className="mb-4 text-sm text-gray-500">The change will be returned to the editor to revise & resubmit.</p>
        <div className="mb-4 rounded-md border border-gray-200 bg-[#F8FAFC] px-3 py-2 text-sm text-gray-600">
          <span>{change?.description || 'Code sheet change'}</span>
        </div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Reason for rejection <span className="text-red-600">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(event) => {
            setReason(event.target.value)
            if (error) setError(null)
          }}
          rows={4}
          disabled={saving}
          className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
              : 'border-gray-300 focus:border-[#1D5176] focus:ring-[#D7E8F5]'
          }`}
        />
        {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
        <p className="mt-3 text-xs text-gray-400">This reason is shared with the editor and logged on the change.</p>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button size="md" variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="md"
          variant="primary"
          className="bg-[#D92D20] hover:bg-[#B42318]"
          onClick={() => void handleConfirm()}
          disabled={saving}
        >
          Confirm Reject
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex h-full flex-col items-center justify-center py-12 text-gray-500">
    <div className="mb-3 h-10 w-10 rounded-full bg-gray-100" aria-hidden="true" />
    <Typography variant="body2" className="text-center font-medium">
      {label}
    </Typography>
  </div>
)

export default function ChangesRequestedDrawer({
  open,
  onClose,
  codeIndexId,
  projectId,
  refreshKey = 0,
  hasApprovalPermission = true,
  approvalDisabledReason = null,
  onRefresh,
  canReviseRejectedChanges = false,
  revisionDisabledReason = null,
  onReviseChange,
  onGoToChange,
  canWithdrawChanges = true,
  withdrawDisabledReason = null,
  resolveAppendixLabel,
}: ChangesRequestedDrawerProps) {
  const [pullRequests, setPullRequests] = useState<PullRequestDTO[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isRestoring, setIsRestoring] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [reviewActionStates, setReviewActionStates] = useState<Record<string, ReviewActionState>>({})
  const reviewInFlight = useRef(new Set<string>())
  const reviewCompletedPendingRefresh = useRef(false)
  const loadedScope = useRef<string | null>(null)
  const [activeTab, setActiveTab] = useState<ChangeStatus>('pending')
  const [rejectTarget, setRejectTarget] = useState<Change | null>(null)
  const requestScope = codeIndexId ? `${codeIndexId}:${projectId || ''}:${refreshKey}` : null

  const loadPullRequests = useCallback(async () => {
    if (!codeIndexId || !requestScope) return []

    if (loadedScope.current !== requestScope) setPullRequests([])
    setIsLoading(true)
    setLoadError(null)
    try {
      const data = await fetchPullRequests(codeIndexId, projectId)
      setPullRequests(data)
      loadedScope.current = requestScope
      return data
    } catch (error) {
      console.error('Failed to load pull requests:', error)
      setLoadError(getApiMessage(error) || 'Failed to load changes. Please try again.')
      return []
    } finally {
      setIsLoading(false)
    }
  }, [codeIndexId, projectId, requestScope])

  useEffect(() => {
    if (!open || !codeIndexId || !requestScope) return

    setActiveTab('pending')
    if (loadedScope.current !== requestScope) void loadPullRequests()
  }, [open, codeIndexId, loadPullRequests, requestScope])

  const changes = useMemo(() => mapPullRequestChanges(pullRequests, resolveAppendixLabel), [pullRequests, resolveAppendixLabel])
  const counts = useMemo(() => ({
    pending: changes.filter(change => change.status === 'pending').length,
    approved: changes.filter(change => change.status === 'approved').length,
    rejected: changes.filter(change => change.status === 'rejected').length,
  }), [changes])
  const filteredChanges = useMemo(
    () => changes.filter(change => change.status === activeTab),
    [activeTab, changes]
  )

  useEffect(() => {
    if (counts.pending !== 0 || !reviewCompletedPendingRefresh.current) return

    reviewCompletedPendingRefresh.current = false
    void (async () => {
      // Only reconcile with the server after the last local pending change is resolved.
      await loadPullRequests()
      await onRefresh?.()
    })()
  }, [counts.pending, loadPullRequests, onRefresh])

  const handleReviewAction = useCallback(async (
    change: Change,
    action: ReviewAction,
    comment: string | null
  ) => {
    if (!codeIndexId || !change.pullRequestId || !change.id) return

    const actionKey = getReviewActionKey(change)
    if (reviewInFlight.current.has(actionKey)) return
    reviewInFlight.current.add(actionKey)
    setReviewActionStates(previous => ({
      ...previous,
      [actionKey]: { action, phase: 'processing', error: null },
    }))

    try {
      const response = await reviewPullRequestChanges(codeIndexId, change.pullRequestId, {
        reviews: [
          {
            changeId: change.id,
            decision: action === 'approve' ? 'Approved' : 'Rejected',
            comment,
          },
        ],
      })

      setReviewActionStates(previous => ({
        ...previous,
        [actionKey]: { action, phase: 'success', error: null },
      }))
      await waitForReviewSuccessIndicator()
      reviewCompletedPendingRefresh.current = true
      setPullRequests(previous => applyLocalReviewDecision(
        previous,
        change,
        action === 'approve' ? 'Approved' : 'Rejected',
        comment
      ))
      setReviewActionStates(previous => {
        const next = { ...previous }
        delete next[actionKey]
        return next
      })
      if (change.status === 'approved' && action === 'reject') {
        setPullRequests(previous => previous.map(pr => pr.id === change.pullRequestId ? response : pr))
        await onRefresh?.()
      }
    } catch (error) {
      console.error(`Failed to ${action} pull request change:`, error)
      setReviewActionStates(previous => ({
        ...previous,
        [actionKey]: {
          action,
          phase: 'error',
          error: getApiMessage(error) || ((error as { response?: { status?: number } })?.response?.status === 409
            ? 'The live Code Sheet changed. Reject this change so its author can resolve the conflict.'
            : 'Action failed. Please try again.'),
        },
      }))
      if ((error as { response?: { status?: number } })?.response?.status === 409) {
        await loadPullRequests()
        await onRefresh?.()
      }
    } finally {
      reviewInFlight.current.delete(actionKey)
    }
  }, [codeIndexId, loadPullRequests, onRefresh])

  const handleResolveConflict = useCallback(async (change: Change, resolution: 'UseMain' | 'KeepProject') => {
    if (!codeIndexId || !change.conflict) return
    const confirmed = await showConfirmModal({
      title: resolution === 'UseMain' ? 'Use Main value?' : 'Keep project value?',
      description: resolution === 'UseMain'
        ? 'The project will adopt the current Main value and this change will be withdrawn.'
        : 'Your project value will be submitted for a new review against the Main value shown here. A later Main change will require resolving it again.',
      confirmText: resolution === 'UseMain' ? 'Use Main' : 'Keep & Resubmit', cancelText: 'Cancel',
    })
    if (!confirmed) return
    setIsRestoring(true)
    try {
      await resolvePullRequestConflict(codeIndexId, change.pullRequestId, change.id, {
        resolution, expectedLiveValue: change.conflict.liveValue, expectedProjectValue: change.conflict.projectValue,
      })
      await loadPullRequests()
      await onRefresh?.()
    } catch (error) {
      await showErrorModal({ title: 'Unable to Resolve Conflict', description: getApiMessage(error) || 'The values may have changed. Refresh and resolve the conflict again.', confirmText: 'OK' })
      await loadPullRequests()
    } finally {
      setIsRestoring(false)
    }
  }, [codeIndexId, loadPullRequests, onRefresh])

  const handleApproveChange = useCallback(
    (change: Change) => handleReviewAction(change, 'approve', null),
    [handleReviewAction]
  )

  const handleRejectChange = useCallback(async (reason: string) => {
    const change = rejectTarget
    if (!change) return

    // Close the reason dialog as soon as the request starts so other changes remain actionable.
    setRejectTarget(null)
    await handleReviewAction(change, 'reject', reason)
  }, [handleReviewAction, rejectTarget])

  const handleReviseChange = useCallback(async (change: Change) => {
    if (!codeIndexId || !change.pullRequestId) return

    if (!isDeleteChange(change.changeType)) {
      await onReviseChange?.(change)
      onClose()
      return
    }

    setIsRestoring(true)
    showLoadingModal({ title: 'Restoring Change', message: 'Restoring the deleted content...' })
    try {
      await restoreDeletedPullRequestChange(codeIndexId, change.pullRequestId, change.id)
      closeLoadingModal()
      await showSuccessModal({
        title: 'Change Restored',
        description: 'The deleted content was restored and the rejected delete change was withdrawn.',
        confirmText: 'OK',
      })
      await loadPullRequests()
      await onRefresh?.()
    } catch (error) {
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Restore Change',
        description: getApiMessage(error) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setIsRestoring(false)
    }
  }, [codeIndexId, loadPullRequests, onClose, onRefresh, onReviseChange])

  const handleWithdrawChange = useCallback(async (change: Change) => {
    if (!codeIndexId || !change.pullRequestId || !change.id) return

    const confirmed = await showConfirmModal({
      title: 'Withdraw change',
      description: 'This change will be removed from the pull request and will no longer be reviewed. Do you want to proceed?',
      confirmText: 'Withdraw',
      cancelText: 'Cancel',
      icon: 'warning',
    })
    if (!confirmed) return

    setIsWithdrawing(true)
    showLoadingModal({
      title: 'Withdrawing Change',
      message: 'Please wait while the change is being withdrawn...',
    })

    try {
      const response = await withdrawPullRequestChange(codeIndexId, change.pullRequestId, change.id)
      closeLoadingModal()
      await showSuccessModal({
        title: 'Change Withdrawn',
        description: response.message || 'Change withdrawn.',
        confirmText: 'OK',
      })
      await loadPullRequests()
      await onRefresh?.()
    } catch (error) {
      console.error('Failed to withdraw pull request change:', error)
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Withdraw Change',
        description: getApiMessage(error) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setIsWithdrawing(false)
    }
  }, [codeIndexId, loadPullRequests, onRefresh])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { invisible: true } }}
      sx={{
        '& .MuiDrawer-paper': {
          width: '420px',
          minWidth: 360,
          maxWidth: 480,
          boxSizing: 'border-box',
        },
      }}
    >
      <div className="flex h-full flex-col bg-white">
        <div className="bg-white px-4 py-4">
          <div className="mb-3 flex items-center justify-between">
            <Typography variant="h6" className="font-semibold text-gray-900">
              Changes requested
            </Typography>
            <div className="flex items-center gap-1">
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    onClick={() => void loadPullRequests()}
                    disabled={isLoading}
                    sx={{ color: '#6b7280', '&:hover': { backgroundColor: '#f3f4f6' } }}
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </IconButton>
            </div>
          </div>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                minHeight: 36,
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1D5176',
                },
              }}
            >
              <Tab
                label={`Pending (${counts.pending})`}
                value="pending"
                sx={{ minHeight: 36, px: 0, mr: 2, textTransform: 'none', '&.Mui-selected': { color: '#1D5176' } }}
              />
              <Tab
                label={`Resolved (${counts.approved})`}
                value="approved"
                sx={{ minHeight: 36, px: 0, mr: 2, textTransform: 'none', '&.Mui-selected': { color: '#1D5176' } }}
              />
              <Tab
                label={`Rejected (${counts.rejected})`}
                value="rejected"
                sx={{ minHeight: 36, px: 0, textTransform: 'none', '&.Mui-selected': { color: '#1D5176' } }}
              />
            </Tabs>
          </Box>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4">
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center py-12">
              <CircularProgress size={40} sx={{ color: '#1D5176' }} />
              <Typography variant="body2" className="mt-3 text-gray-500">
                Loading changes...
              </Typography>
            </div>
          ) : loadError ? (
            <div className="flex h-full flex-col items-center justify-center py-12 text-gray-500">
              <Typography variant="body2" className="mb-3 text-center text-red-500">
                {loadError}
              </Typography>
              <Button size="sm" variant="secondary" onClick={() => void loadPullRequests()}>
                Try Again
              </Button>
            </div>
          ) : !codeIndexId ? (
            <EmptyState label="No Code Sheet selected" />
          ) : changes.length === 0 ? (
            <EmptyState label="There's no request pending" />
          ) : filteredChanges.length === 0 ? (
            <EmptyState label={activeTab === 'pending' ? 'No pending changes' : activeTab === 'approved' ? 'No resolved changes yet' : 'No rejected changes'} />
          ) : (
            <div className="space-y-3">
              {filteredChanges.map((change) => (
                <ChangeReviewCard
                  key={`${change.pullRequestId}-${change.id}`}
                  change={change}
                  hasPermission={hasApprovalPermission}
                  approvalDisabledReason={approvalDisabledReason}
                  isProcessing={isRestoring || isWithdrawing}
                  reviewActionState={reviewActionStates[getReviewActionKey(change)]}
                  onApprove={handleApproveChange}
                  onReject={setRejectTarget}
                  onGoToChange={onGoToChange}
                  canRevise={canReviseRejectedChanges}
                  revisionDisabledReason={revisionDisabledReason}
                  onRevise={handleReviseChange}
                  canWithdraw={canWithdrawChanges}
                  withdrawDisabledReason={withdrawDisabledReason}
                  onWithdraw={handleWithdrawChange}
                  onResolve={handleResolveConflict}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <RejectChangeDialog
        open={Boolean(rejectTarget)}
        change={rejectTarget}
        saving={false}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleRejectChange}
      />
    </Drawer>
  )
}
